import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useHistory, useLocation } from "react-router-dom";
import {
	getSystemVersion,
	formatSystemReleaseDateBr,
	getSystemReleaseDate,
} from "../../helpers/systemInfo";

const SIDEBAR_WIDTH = 260;
const RIGHT_PANEL_WIDTH = 380;
const HEADER_HEIGHT = 56;
const GAP = 16;

const DOCS_VERSION = getSystemVersion();
const DOCS_RELEASE_DATE = formatSystemReleaseDateBr(getSystemReleaseDate());
const CONTATOS_API_LAST_UPDATE = DOCS_RELEASE_DATE || "—";

const BASE_URL = "https://app.pluggme.com.br/api";

const SECTIONS = [
	"introducao",
	"contatos",
	"funil-vendas",
	"erros",
];

const INTRO_SUB = [
	{ id: "primeiros-passos", label: "Primeiros passos" },
	{ id: "autenticacao", label: "Autenticação" },
];

const navItems = [
	{ id: "introducao", label: "Introdução" },
	{ id: "contatos", label: "Contatos" },
	{ id: "funil-vendas", label: "Funil de vendas" },
	{ id: "erros", label: "Erros" },
];

const CONTATOS_SUB = [
	{ id: "listar", label: "Listar contatos" },
	{ id: "obter", label: "Obter um contato" },
	{ id: "criar", label: "Criar contato" },
	{ id: "atualizar", label: "Atualizar contato" },
	{ id: "remover", label: "Remover contato" },
	{ id: "bulk-create", label: "Criação em massa" },
];

const FUNIL_SUB = [
	{ id: "listar", label: "Listar funis" },
	{ id: "criar", label: "Criar funil" },
	{ id: "atualizar", label: "Atualizar funil" },
	{ id: "remover", label: "Remover funil" },
	{ id: "pin-contact", label: "Fixar contato" },
	{ id: "unpin-contact", label: "Desfixar contato" },
];

const FUNIL_API_LAST_UPDATE = DOCS_RELEASE_DATE;

const STORAGE_DARK_KEY = "docsDarkMode";

const MoonIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
	</svg>
);
const SunIcon = () => (
	<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
		<circle cx="12" cy="12" r="5" />
		<line x1="12" y1="1" x2="12" y2="3" />
		<line x1="12" y1="21" x2="12" y2="23" />
		<line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
		<line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
		<line x1="1" y1="12" x2="3" y2="12" />
		<line x1="21" y1="12" x2="23" y2="12" />
		<line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
		<line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
	</svg>
);

const styles = {
	root: {
		height: "100vh",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#f1f5f9",
		backgroundImage: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)",
		fontFamily: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
	},
	header: {
		position: "fixed",
		top: 0,
		left: 0,
		right: 0,
		height: HEADER_HEIGHT,
		backgroundColor: "rgba(255,255,255,0.92)",
		backdropFilter: "blur(12px)",
		WebkitBackdropFilter: "blur(12px)",
		boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "0 1.5rem",
		zIndex: 100,
		borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
	},
	headerTitle: {
		fontSize: "1.125rem",
		fontWeight: 600,
		color: "#0f172a",
		margin: 0,
		letterSpacing: "-0.02em",
	},
	headerVersion: {
		fontSize: "0.75rem",
		fontWeight: 600,
		fontFamily: "ui-monospace, SF Mono, Consolas, monospace",
		color: "#4f46e5",
		backgroundColor: "rgba(79, 70, 229, 0.1)",
		padding: "0.35rem 0.65rem",
		borderRadius: "8px",
		border: "1px solid rgba(79, 70, 229, 0.2)",
	},
	headerRight: {
		display: "flex",
		alignItems: "center",
		gap: "0.75rem",
	},
	headerDarkToggle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "0.35rem 0.65rem",
		border: "1px solid #e2e8f0",
		borderRadius: "8px",
		backgroundColor: "#fff",
		color: "#475569",
		cursor: "pointer",
		transition: "color 0.2s, background 0.2s, border-color 0.2s",
	},
	headerDarkToggleDark: {
		borderColor: "rgba(255,255,255,0.2)",
		backgroundColor: "rgba(255,255,255,0.08)",
		color: "#e2e8f0",
	},
	layout: {
		flex: 1,
		display: "flex",
		minHeight: 0,
		overflow: "hidden",
		paddingTop: HEADER_HEIGHT + GAP,
		paddingLeft: SIDEBAR_WIDTH + GAP * 2,
		paddingRight: RIGHT_PANEL_WIDTH + GAP,
		paddingBottom: GAP,
		position: "relative",
		boxSizing: "border-box",
	},
	sidebar: {
		position: "fixed",
		left: GAP,
		top: HEADER_HEIGHT + GAP,
		width: SIDEBAR_WIDTH,
		height: `calc(100vh - ${HEADER_HEIGHT}px - ${GAP * 2}px)`,
		backgroundColor: "#fff",
		borderRadius: "14px",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
		border: "1px solid rgba(226, 232, 240, 0.8)",
		overflowY: "auto",
		overflowX: "hidden",
		padding: "1.25rem 0",
		zIndex: 50,
	},
	navGroupTitle: {
		fontSize: "0.6875rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.08em",
		color: "#94a3b8",
		padding: "0 1rem",
		marginBottom: "0.5rem",
		marginTop: "1rem",
	},
	navLink: {
		display: "block",
		padding: "0.55rem 1rem",
		fontSize: "0.875rem",
		color: "#475569",
		textDecoration: "none",
		borderLeft: "3px solid transparent",
		transition: "color 0.15s, background 0.15s, border-color 0.15s",
		outline: "none",
		borderRadius: "0 8px 8px 0",
		marginLeft: "4px",
	},
	navLinkActive: {
		color: "#4f46e5",
		fontWeight: 600,
		backgroundColor: "rgba(79, 70, 229, 0.08)",
		borderLeft: "3px solid #4f46e5",
	},
	navSubLink: {
		display: "block",
		padding: "0.4rem 1rem 0.4rem 1.75rem",
		fontSize: "0.8125rem",
		color: "#64748b",
		textDecoration: "none",
		borderLeft: "3px solid transparent",
		transition: "color 0.15s, background 0.15s",
		borderRadius: "0 8px 8px 0",
		marginLeft: "4px",
	},
	navSubLinkActive: {
		color: "#4f46e5",
		fontWeight: 500,
		backgroundColor: "rgba(79, 70, 229, 0.08)",
		borderLeft: "3px solid #4f46e5",
	},
	content: {
		flex: 1,
		minHeight: 0,
		minWidth: 0,
		height: `calc(100vh - ${HEADER_HEIGHT}px - ${GAP * 2}px)`,
		maxWidth: "100%",
		marginRight: GAP,
		overflowY: "auto",
		overflowX: "hidden",
		WebkitOverflowScrolling: "touch",
		padding: "2.25rem 2.5rem 4rem",
		backgroundColor: "#fff",
		borderRadius: "14px",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
		border: "1px solid rgba(226, 232, 240, 0.8)",
		boxSizing: "border-box",
	},
	rightPanel: {
		position: "fixed",
		right: GAP,
		top: HEADER_HEIGHT + GAP,
		width: RIGHT_PANEL_WIDTH,
		height: `calc(100vh - ${HEADER_HEIGHT}px - ${GAP * 2}px)`,
		backgroundColor: "#fff",
		borderRadius: "14px",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.06), 0 2px 4px -2px rgba(0,0,0,0.04)",
		border: "1px solid rgba(226, 232, 240, 0.8)",
		overflowY: "auto",
		overflowX: "hidden",
		padding: "1.5rem 1.25rem 1.5rem",
		zIndex: 40,
	},
	rightPanelTitle: {
		fontSize: "0.6875rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.08em",
		color: "#94a3b8",
		marginBottom: "1rem",
		padding: "0 0.25rem",
	},
	rightPanelLabel: {
		fontSize: "0.75rem",
		fontWeight: 600,
		color: "#475569",
		marginTop: "1rem",
		marginBottom: "0.4rem",
	},
	rightPanelCodeWrap: {
		position: "relative",
		marginBottom: "0.75rem",
		borderRadius: "10px",
		overflow: "hidden",
		boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
		border: "1px solid rgba(30, 41, 59, 0.2)",
	},
	rightPanelCode: {
		backgroundColor: "#1e293b",
		color: "#e2e8f0",
		padding: "0.85rem 2.5rem 0.85rem 1rem",
		borderRadius: "10px",
		fontFamily: "ui-monospace, SF Mono, Consolas, monospace",
		fontSize: "0.6875rem",
		overflowX: "auto",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		lineHeight: 1.5,
	},
	rightPanelCodeCopyBtn: {
		position: "absolute",
		top: "0.35rem",
		right: "0.35rem",
		width: "28px",
		height: "28px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		backgroundColor: "#334155",
		border: "none",
		borderRadius: "0.25rem",
		cursor: "pointer",
		color: "#e2e8f0",
		transition: "color 0.15s, background 0.15s",
	},
	rightPanelCodeCopyBtnHover: {
		color: "#f1f5f9",
		backgroundColor: "#475569",
	},
	rightPanelCodeCopied: {
		fontSize: "0.875rem",
		color: "#34d399",
		fontWeight: 600,
	},
	section: { marginBottom: "2.5rem" },
	lastUpdate: {
		fontSize: "0.8125rem",
		color: "#94a3b8",
		marginTop: "0.25rem",
		marginBottom: "0.75rem",
	},
	h1: {
		fontSize: "2rem",
		fontWeight: 700,
		color: "#0f172a",
		marginBottom: "0.5rem",
		letterSpacing: "-0.025em",
		lineHeight: 1.25,
	},
	h2: {
		fontSize: "1.5rem",
		fontWeight: 600,
		color: "#0f172a",
		marginTop: "1.5rem",
		marginBottom: "0.75rem",
		paddingBottom: "0.5rem",
		borderBottom: "2px solid #e2e8f0",
		letterSpacing: "-0.02em",
	},
	h2First: { marginTop: 0 },
	h3: {
		fontSize: "1.125rem",
		fontWeight: 600,
		color: "#334155",
		marginTop: "1.5rem",
		marginBottom: "0.6rem",
		letterSpacing: "-0.01em",
	},
	paragraph: {
		color: "#475569",
		fontSize: "0.9375rem",
		lineHeight: 1.75,
		marginBottom: "1rem",
	},
	inlineCode: {
		backgroundColor: "#f1f5f9",
		padding: "0.2rem 0.45rem",
		borderRadius: "6px",
		fontFamily: "ui-monospace, SF Mono, Consolas, monospace",
		fontSize: "0.875em",
		color: "#334155",
		border: "1px solid #e2e8f0",
	},
	codeBlock: {
		backgroundColor: "#0f172a",
		color: "#e2e8f0",
		padding: "1.35rem 1.5rem",
		borderRadius: "12px",
		fontFamily: "ui-monospace, SF Mono, Consolas, Monaco, monospace",
		fontSize: "0.8125rem",
		overflowX: "auto",
		margin: "0.75rem 0 1.25rem",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		lineHeight: 1.6,
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.05)",
		border: "1px solid rgba(30, 41, 59, 0.5)",
	},
	codeBlockWrap: {
		marginTop: "0.5rem",
		marginBottom: "1.25rem",
		borderRadius: "12px",
		overflow: "hidden",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.08)",
		border: "1px solid rgba(30, 41, 59, 0.4)",
	},
	codeBlockLabel: {
		fontSize: "0.6875rem",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.08em",
		color: "#94a3b8",
		backgroundColor: "#1e293b",
		padding: "0.5rem 1rem",
		borderBottom: "1px solid rgba(255,255,255,0.06)",
	},
	codeBlockInner: {
		backgroundColor: "#0f172a",
		color: "#e2e8f0",
		padding: "1.25rem 1.5rem",
		margin: 0,
		borderRadius: 0,
		boxShadow: "none",
		border: "none",
		fontFamily: "ui-monospace, SF Mono, Consolas, Monaco, monospace",
		fontSize: "0.8125rem",
		overflowX: "auto",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		lineHeight: 1.6,
	},
	table: {
		width: "100%",
		borderCollapse: "collapse",
		margin: "1rem 0 1.25rem",
		fontSize: "0.875rem",
		borderRadius: "12px",
		overflow: "hidden",
		boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
		border: "1px solid #e2e8f0",
	},
	th: {
		textAlign: "left",
		padding: "0.85rem 1.15rem",
		backgroundColor: "#f8fafc",
		fontWeight: 600,
		color: "#334155",
		borderBottom: "2px solid #e2e8f0",
		fontSize: "0.8125rem",
	},
	td: {
		padding: "0.85rem 1.15rem",
		backgroundColor: "#fff",
		color: "#475569",
		borderBottom: "1px solid #f1f5f9",
	},
	tdAlt: {
		padding: "0.85rem 1.15rem",
		backgroundColor: "#f8fafc",
		color: "#475569",
		borderBottom: "1px solid #f1f5f9",
	},
	badgeGet: {
		backgroundColor: "rgba(5, 150, 105, 0.12)",
		color: "#059669",
		padding: "0.25rem 0.6rem",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "0.75rem",
		display: "inline-block",
		border: "1px solid rgba(5, 150, 105, 0.25)",
	},
	badgePost: {
		backgroundColor: "rgba(37, 99, 235, 0.12)",
		color: "#2563eb",
		padding: "0.25rem 0.6rem",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "0.75rem",
		display: "inline-block",
		border: "1px solid rgba(37, 99, 235, 0.25)",
	},
	badgePut: {
		backgroundColor: "rgba(217, 119, 6, 0.12)",
		color: "#d97706",
		padding: "0.25rem 0.6rem",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "0.75rem",
		display: "inline-block",
		border: "1px solid rgba(217, 119, 6, 0.25)",
	},
	badgeDelete: {
		backgroundColor: "rgba(220, 38, 38, 0.12)",
		color: "#dc2626",
		padding: "0.25rem 0.6rem",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "0.75rem",
		display: "inline-block",
		border: "1px solid rgba(220, 38, 38, 0.25)",
	},
	badgeError: {
		backgroundColor: "rgba(185, 28, 28, 0.12)",
		color: "#b91c1c",
		padding: "0.25rem 0.6rem",
		borderRadius: "6px",
		fontWeight: 600,
		fontSize: "0.75rem",
		display: "inline-block",
		fontFamily: "ui-monospace, monospace",
		border: "1px solid rgba(185, 28, 28, 0.25)",
	},
	ul: {
		margin: "0.5rem 0 1rem",
		paddingLeft: "1.5rem",
		color: "#475569",
		fontSize: "0.9375rem",
		lineHeight: 1.7,
	},
	note: {
		backgroundColor: "#eff6ff",
		borderLeft: "4px solid #3b82f6",
		padding: "1rem 1.25rem",
		borderRadius: "0 0.375rem 0.375rem 0",
		fontSize: "0.875rem",
		color: "#1e40af",
		margin: "1rem 0",
		lineHeight: 1.6,
	},
};

const darkOverrides = {
	root: {
		backgroundColor: "#0f172a",
		backgroundImage: "none",
	},
	header: {
		backgroundColor: "rgba(15, 23, 42, 0.95)",
		borderBottom: "1px solid rgba(255,255,255,0.08)",
		boxShadow: "0 1px 0 rgba(255,255,255,0.05)",
	},
	headerTitle: { color: "#f1f5f9" },
	headerVersion: {
		color: "#a5b4fc",
		backgroundColor: "rgba(79, 70, 229, 0.25)",
		border: "1px solid rgba(79, 70, 229, 0.4)",
	},
	layout: {},
	sidebar: {
		backgroundColor: "#1e293b",
		border: "1px solid rgba(255,255,255,0.08)",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.2)",
	},
	navGroupTitle: { color: "#94a3b8" },
	navLink: { color: "#cbd5e1" },
	navLinkActive: {
		color: "#a5b4fc",
		backgroundColor: "rgba(79, 70, 229, 0.15)",
		borderLeft: "3px solid #818cf8",
	},
	navSubLink: { color: "#94a3b8" },
	navSubLinkActive: {
		color: "#a5b4fc",
		backgroundColor: "rgba(79, 70, 229, 0.15)",
		borderLeft: "3px solid #818cf8",
	},
	content: {
		backgroundColor: "#1e293b",
		border: "1px solid rgba(255,255,255,0.08)",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.2)",
	},
	rightPanel: {
		backgroundColor: "#1e293b",
		border: "1px solid rgba(255,255,255,0.08)",
		boxShadow: "0 4px 6px -1px rgba(0,0,0,0.2)",
	},
	rightPanelTitle: { color: "#94a3b8" },
	rightPanelLabel: { color: "#cbd5e1" },
	rightPanelCodeWrap: {
		border: "1px solid rgba(255,255,255,0.18)",
		boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
		borderRadius: "10px",
	},
	rightPanelCode: {
		backgroundColor: "#0f172a",
		color: "#f1f5f9",
		borderRadius: "10px",
	},
	rightPanelCodeCopyBtn: {
		backgroundColor: "#334155",
		color: "#e2e8f0",
	},
	rightPanelCodeCopyBtnHover: {
		backgroundColor: "#475569",
		color: "#f1f5f9",
	},
	rightPanelCodeCopied: { color: "#34d399" },
	section: {},
	lastUpdate: { color: "#94a3b8" },
	h1: { color: "#f1f5f9" },
	h2: { color: "#f1f5f9", borderBottom: "2px solid rgba(255,255,255,0.1)" },
	h3: { color: "#e2e8f0" },
	paragraph: { color: "#cbd5e1" },
	inlineCode: {
		backgroundColor: "rgba(255,255,255,0.1)",
		color: "#e2e8f0",
		border: "1px solid rgba(255,255,255,0.15)",
	},
	table: { border: "1px solid rgba(255,255,255,0.08)" },
	th: {
		backgroundColor: "rgba(255,255,255,0.06)",
		color: "#e2e8f0",
		borderBottom: "2px solid rgba(255,255,255,0.1)",
	},
	td: {
		backgroundColor: "#1e293b",
		color: "#cbd5e1",
		borderBottom: "1px solid rgba(255,255,255,0.06)",
	},
	tdAlt: {
		backgroundColor: "rgba(255,255,255,0.03)",
		color: "#cbd5e1",
		borderBottom: "1px solid rgba(255,255,255,0.06)",
	},
	note: {
		backgroundColor: "rgba(59, 130, 246, 0.15)",
		borderLeft: "4px solid #60a5fa",
		color: "#93c5fd",
	},
	codeBlockWrap: {
		border: "1px solid rgba(255,255,255,0.18)",
		boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
	},
	codeBlockLabel: {
		backgroundColor: "#334155",
		color: "#e2e8f0",
		borderBottom: "1px solid rgba(255,255,255,0.12)",
	},
	codeBlockInner: {
		backgroundColor: "#0f172a",
		color: "#f1f5f9",
		borderTop: "1px solid rgba(255,255,255,0.06)",
	},
};

function mergeTheme(base, overrides) {
	const out = { ...base };
	Object.keys(overrides).forEach((k) => {
		if (base[k] && typeof base[k] === "object" && !Array.isArray(base[k]) && overrides[k] && typeof overrides[k] === "object") {
			out[k] = { ...base[k], ...overrides[k] };
		} else if (overrides[k] !== undefined) {
			out[k] = overrides[k];
		}
	});
	return out;
}

function CodeBlock({ children, label, themeStyles }) {
	const s = themeStyles || styles;
	if (label) {
		return (
			<div style={s.codeBlockWrap}>
				<div style={s.codeBlockLabel}>{label}</div>
				<pre style={s.codeBlockInner}>{children}</pre>
			</div>
		);
	}
	return <pre style={s.codeBlock}>{children}</pre>;
}

const copyIcon = (
	<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
		<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
	</svg>
);

function CodeWithCopy({ text, themeStyles }) {
	const s = themeStyles || styles;
	const [copied, setCopied] = useState(false);
	const [hover, setHover] = useState(false);
	const handleCopy = () => {
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	};
	return (
		<div style={s.rightPanelCodeWrap}>
			<pre style={s.rightPanelCode}>{text}</pre>
			<button
				type="button"
				onClick={handleCopy}
				onMouseEnter={() => setHover(true)}
				onMouseLeave={() => setHover(false)}
				style={{
					...s.rightPanelCodeCopyBtn,
					...(hover ? s.rightPanelCodeCopyBtnHover : {}),
				}}
				title="Copiar código"
				aria-label="Copiar código"
			>
				{copied ? (
					<span style={s.rightPanelCodeCopied}>✓</span>
				) : (
					copyIcon
				)}
			</button>
		</div>
	);
}

function RightPanelExamples({ section, baseUrl, introSub, contatosSub, funilSub, themeStyles }) {
	const s = themeStyles || styles;
	const label = (t) => <div style={s.rightPanelLabel}>{t}</div>;
	const code = (c) => <CodeWithCopy text={c} themeStyles={s} />;

	if (section === "contatos") {
		const c = contatosSub || "listar";
		if (c === "listar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Listar contatos</div>
					{label("cURL")}
					{code(`curl -X GET "${baseUrl}/contacts" \\
  -H "Authorization: Bearer SEU_TOKEN"`)}
					{label("JavaScript")}
					{code(`const res = await fetch("${baseUrl}/contacts", {
  headers: { "Authorization": \`Bearer \${token}\` }
});
const { contacts, count, hasMore } = await res.json();`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer " . $token]);
$json = curl_exec($ch);
$data = json_decode($json, true);`)}
				</>
			);
		}
		if (c === "obter") {
			return (
				<>
					<div style={s.rightPanelTitle}>Obter um contato</div>
					{label("cURL")}
					{code(`curl -X GET "${baseUrl}/contacts/CONTACT_ID" \\
  -H "Authorization: Bearer SEU_TOKEN"`)}
					{label("JavaScript")}
					{code(`const res = await fetch(\`${baseUrl}/contacts/\${contactId}\`, {
  headers: { "Authorization": \`Bearer \${token}\` }
});
const contact = await res.json();`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts/" . $contactId);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer " . $token]);
$contact = json_decode(curl_exec($ch), true);`)}
				</>
			);
		}
		if (c === "criar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Criar contato</div>
					{label("cURL")}
					{code(`curl -X POST "${baseUrl}/contacts" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"João","number":"5511999999999","email":"joao@email.com"}'`)}
					{label("JavaScript")}
					{code(`await fetch("${baseUrl}/contacts", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ name: "João", number: "5511999999999", email: "joao@email.com" })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
  "name" => "João", "number" => "5511999999999", "email" => "joao@email.com"
]));
curl_exec($ch);`)}
				</>
			);
		}
		if (c === "atualizar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Atualizar contato</div>
					{label("cURL")}
					{code(`curl -X PUT "${baseUrl}/contacts/CONTACT_ID" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Novo Nome","email":"novo@email.com"}'`)}
					{label("JavaScript")}
					{code(`await fetch(\`${baseUrl}/contacts/\${contactId}\`, {
  method: "PUT",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ name: "Novo Nome", email: "novo@email.com" })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts/" . $contactId);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "PUT");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["name" => "Novo Nome", "email" => "novo@email.com"]));
curl_exec($ch);`)}
				</>
			);
		}
		if (c === "remover") {
			return (
				<>
					<div style={s.rightPanelTitle}>Remover contato</div>
					{label("cURL")}
					{code(`curl -X DELETE "${baseUrl}/contacts/CONTACT_ID" \\
  -H "Authorization: Bearer SEU_TOKEN"`)}
					{label("JavaScript")}
					{code(`await fetch(\`${baseUrl}/contacts/\${contactId}\`, {
  method: "DELETE",
  headers: { "Authorization": \`Bearer \${token}\` }
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts/" . $contactId);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "DELETE");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer " . $token]);
curl_exec($ch);`)}
				</>
			);
		}
		if (c === "bulk-create") {
			return (
				<>
					<div style={s.rightPanelTitle}>Criação em massa</div>
					{label("cURL")}
					{code(`curl -X POST "${baseUrl}/contacts/bulk-create" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"contacts":[{"name":"João","number":"5511999999999"},{"name":"Maria","number":"5511888888888"}]}'`)}
					{label("JavaScript")}
					{code(`await fetch("${baseUrl}/contacts/bulk-create", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    contacts: [
      { name: "João", number: "5511999999999" },
      { name: "Maria", number: "5511888888888" }
    ]
  })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/contacts/bulk-create");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
  "contacts" => [
    ["name" => "João", "number" => "5511999999999"],
    ["name" => "Maria", "number" => "5511888888888"]
  ]
]));
curl_exec($ch);`)}
				</>
			);
		}
	}
	if (section === "introducao" && introSub === "autenticacao") {
		return (
			<>
				<div style={s.rightPanelTitle}>API externa: obter token com API Key</div>
				{label("cURL")}
				{code(`curl -X POST "${baseUrl}/auth/token" \\
  -H "X-API-Key: SUA_API_KEY"`)}
				{label("JavaScript")}
				{code(`const res = await fetch("${baseUrl}/auth/token", {
  method: "POST",
  headers: { "X-API-Key": apiKey }
});
const { token, expiresIn } = await res.json();
// Use \`token\` no header: Authorization: Bearer \${token}`)}
				{label("PHP")}
				{code(`$ch = curl_init("${baseUrl}/auth/token");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["X-API-Key: " . $apiKey]);
$data = json_decode(curl_exec($ch), true);
$token = $data["token"];
// Use $token no header: Authorization: Bearer $token`)}
				<div style={{ ...s.rightPanelTitle, marginTop: 16 }}>Login (usuário/senha)</div>
				{label("cURL")}
				{code(`curl -X POST "${baseUrl}/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"seu@email.com","password":"sua_senha"}'`)}
				{label("JavaScript")}
				{code(`const res = await fetch("${baseUrl}/auth/login", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ email: "seu@email.com", password: "sua_senha" })
});
const { token } = await res.json();`)}
				{label("Header nas demais requisições")}
				{code("Authorization: Bearer <token>")}
			</>
		);
	}
	if (section === "funil-vendas") {
		const f = funilSub || "listar";
		if (f === "listar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Listar funis</div>
					{label("cURL")}
					{code(`curl -X GET "${baseUrl}/sales-funnel?workspace=default" \\
  -H "Authorization: Bearer SEU_TOKEN"`)}
					{label("JavaScript")}
					{code(`const res = await fetch("${baseUrl}/sales-funnel?workspace=default", {
  headers: { "Authorization": \`Bearer \${token}\` }
});
const funis = await res.json();`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel?workspace=default");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer " . $token]);
$funis = json_decode(curl_exec($ch), true);`)}
				</>
			);
		}
		if (f === "criar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Criar funil</div>
					{label("cURL")}
					{code(`curl -X POST "${baseUrl}/sales-funnel/column" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Meu Funil", "workspace": "default"}'`)}
					{label("JavaScript")}
					{code(`await fetch("${baseUrl}/sales-funnel/column", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ name: "Meu Funil", workspace: "default" })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel/column");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["name" => "Meu Funil", "workspace" => "default"]));
curl_exec($ch);`)}
				</>
			);
		}
		if (f === "atualizar") {
			return (
				<>
					<div style={s.rightPanelTitle}>Atualizar funil</div>
					{label("cURL")}
					{code(`curl -X PUT "${baseUrl}/sales-funnel/FUNIL_ID" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"name": "Novo Nome", "position": 1}'`)}
					{label("JavaScript")}
					{code(`await fetch(\`${baseUrl}/sales-funnel/\${funilId}\`, {
  method: "PUT",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ name: "Novo Nome", position: 1 })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel/" . $funilId);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "PUT");
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["name" => "Novo Nome", "position" => 1]));
curl_exec($ch);`)}
				</>
			);
		}
		if (f === "remover") {
			return (
				<>
					<div style={s.rightPanelTitle}>Remover funil</div>
					{label("cURL")}
					{code(`curl -X DELETE "${baseUrl}/sales-funnel/FUNIL_ID" \\
  -H "Authorization: Bearer SEU_TOKEN"`)}
					{label("JavaScript")}
					{code(`await fetch(\`${baseUrl}/sales-funnel/\${funilId}\`, {
  method: "DELETE",
  headers: { "Authorization": \`Bearer \${token}\` }
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel/" . $funilId);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "DELETE");
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Authorization: Bearer " . $token]);
curl_exec($ch);`)}
				</>
			);
		}
		if (f === "pin-contact") {
			return (
				<>
					<div style={s.rightPanelTitle}>Fixar contato</div>
					{label("cURL")}
					{code(`curl -X POST "${baseUrl}/sales-funnel/pin-contact" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"contactId": 1, "salesFunnelColumnId": 1}'`)}
					{label("JavaScript")}
					{code(`await fetch("${baseUrl}/sales-funnel/pin-contact", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ contactId: 1, salesFunneColumnlId: 1 })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel/pin-contact");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["contactId" => 1, "salesFunneColumnlId" => 1]));
curl_exec($ch);`)}
				</>
			);
		}
		if (f === "unpin-contact") {
			return (
				<>
					<div style={s.rightPanelTitle}>Desfixar contato</div>
					{label("cURL")}
					{code(`curl -X POST "${baseUrl}/sales-funnel/unpin-contact" \\
  -H "Authorization: Bearer SEU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"contactId": 1, "salesFunnelId": 1}'`)}
					{label("JavaScript")}
					{code(`await fetch("${baseUrl}/sales-funnel/unpin-contact", {
  method: "POST",
  headers: {
    "Authorization": \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ contactId: 1, salesFunnelId: 1 })
});`)}
					{label("PHP")}
					{code(`$ch = curl_init("${baseUrl}/sales-funnel/unpin-contact");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
  "Authorization: Bearer " . $token, "Content-Type: application/json"
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["contactId" => 1, "salesFunnelId" => 1]));
curl_exec($ch);`)}
				</>
			);
		}
		return null;
	}
	if (section === "erros") {
		return (
			<>
				<div style={s.rightPanelTitle}>Resposta de erro</div>
				{label("Exemplo 401")}
				{code(`HTTP/1.1 401 Unauthorized
Content-Type: application/json

{ "error": "ERR_SESSION_EXPIRED" }`)}
			</>
		);
	}
	// introducao (e primeiros passos na mesma página), referencia ou padrão
	return (
		<>
			<div style={s.rightPanelTitle}>Exemplos</div>
			{label("URL base")}
			{code(baseUrl)}
			{label("Login (obter token)")}
			{label("cURL")}
			{code(`curl -X POST "${baseUrl}/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"seu@email.com","password":"sua_senha"}'`)}
			{label("JavaScript")}
			{code(`const res = await fetch("${baseUrl}/auth/login", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ email: "seu@email.com", password: "sua_senha" })
});
const { token } = await res.json();`)}
			{label("PHP")}
			{code(`$ch = curl_init("${baseUrl}/auth/login");
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, ["Content-Type: application/json"]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode(["email" => "seu@email.com", "password" => "sua_senha"]));
$data = json_decode(curl_exec($ch), true);
$token = $data["token"];`)}
		</>
	);
}

const INTRO_SUB_IDS = INTRO_SUB.map((s) => s.id);
const CONTATOS_SUB_IDS = CONTATOS_SUB.map((s) => s.id);
const FUNIL_SUB_IDS = FUNIL_SUB.map((s) => s.id);

export default function DeveloperDocs() {
	const { section } = useParams();
	const history = useHistory();
	const location = useLocation();
	const contentRef = useRef(null);
	const baseUrl = BASE_URL;
	const currentSection = !section || !SECTIONS.includes(section) ? "introducao" : section;
	const hash = (location.hash || "").slice(1);
	const hashIntroSub = currentSection === "introducao" && INTRO_SUB_IDS.includes(hash) ? hash : null;
	const hashContatosSub = currentSection === "contatos" && CONTATOS_SUB_IDS.includes(hash) ? hash : null;
	const hashFunilSub = currentSection === "funil-vendas" && FUNIL_SUB_IDS.includes(hash) ? hash : null;
	const [scrollActiveIntroSub, setScrollActiveIntroSub] = useState(null);
	const [scrollActiveContatosSub, setScrollActiveContatosSub] = useState(null);
	const [scrollActiveFunilSub, setScrollActiveFunilSub] = useState(null);
	const [darkMode, setDarkMode] = useState(() => {
		try { return localStorage.getItem(STORAGE_DARK_KEY) === "true"; } catch { return false; }
	});
	const activeIntroSub = scrollActiveIntroSub ?? hashIntroSub ?? (currentSection === "introducao" ? "primeiros-passos" : null);
	const activeContatosSub = scrollActiveContatosSub ?? hashContatosSub ?? (currentSection === "contatos" ? "listar" : null);
	const activeFunilSub = scrollActiveFunilSub ?? hashFunilSub ?? (currentSection === "funil-vendas" ? "listar" : null);

	const effectiveStyles = React.useMemo(
		() => (darkMode ? mergeTheme(styles, darkOverrides) : styles),
		[darkMode]
	);

	useEffect(() => {
		try { localStorage.setItem(STORAGE_DARK_KEY, String(darkMode)); } catch (_) {}
	}, [darkMode]);

	// Redirecionar /docs, /docs/primeiros-passos, /docs/referencia ou /docs/autenticacao para /docs/introducao (ou #autenticacao)
	useEffect(() => {
		if (!section) {
			history.replace("/docs/introducao");
			return;
		}
		if (section === "autenticacao") {
			history.replace("/docs/introducao#autenticacao");
			return;
		}
		if (!SECTIONS.includes(section) && (section === "primeiros-passos" || section === "referencia")) {
			history.replace("/docs/introducao");
		}
	}, [section, history]);

	// Rolar o conteúdo para o topo ao trocar de seção (não quando só o hash muda)
	useEffect(() => {
		if (contentRef.current && currentSection) {
			contentRef.current.scrollTop = 0;
		}
		if (currentSection !== "introducao") {
			setScrollActiveIntroSub(null);
		}
		if (currentSection !== "contatos") {
			setScrollActiveContatosSub(null);
		}
		if (currentSection !== "funil-vendas") {
			setScrollActiveFunilSub(null);
		}
	}, [currentSection]);

	// Na página Introdução, rolar até a seção do hash ao carregar ou mudar o hash
	useEffect(() => {
		if (currentSection !== "introducao" || !hash || !INTRO_SUB_IDS.includes(hash)) return;
		const el = document.getElementById(hash);
		if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
	}, [currentSection, hash]);

	// Na página Contatos, rolar até a seção do hash ao carregar ou mudar o hash
	useEffect(() => {
		if (currentSection !== "contatos" || !hash || !CONTATOS_SUB_IDS.includes(hash)) return;
		const el = document.getElementById(hash);
		if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
	}, [currentSection, hash]);

	// Na página Funil de vendas, rolar até a seção do hash ao carregar ou mudar o hash
	useEffect(() => {
		if (currentSection !== "funil-vendas" || !hash || !FUNIL_SUB_IDS.includes(hash)) return;
		const el = document.getElementById(hash);
		if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
	}, [currentSection, hash]);

	// Na página Introdução, atualizar o item ativo da sidebar conforme o scroll
	useEffect(() => {
		if (currentSection !== "introducao" || !contentRef.current) return;
		const container = contentRef.current;
		const onScroll = () => {
			const scrollTop = container.scrollTop;
			const scrollHeight = container.scrollHeight;
			const clientHeight = container.clientHeight;
			if (scrollHeight - clientHeight <= scrollTop + 80) {
				setScrollActiveIntroSub(INTRO_SUB_IDS[INTRO_SUB_IDS.length - 1]);
				return;
			}
			const offset = Math.max(150, clientHeight * 0.35);
			let current = null;
			for (const id of INTRO_SUB_IDS) {
				const el = document.getElementById(id);
				if (!el) continue;
				const top = el.offsetTop;
				if (top <= scrollTop + offset) current = id;
			}
			setScrollActiveIntroSub(current || "primeiros-passos");
		};
		onScroll();
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [currentSection]);

	// Na página Contatos, atualizar o item ativo da sidebar conforme o scroll
	useEffect(() => {
		if (currentSection !== "contatos" || !contentRef.current) return;
		const container = contentRef.current;
		const onScroll = () => {
			const scrollTop = container.scrollTop;
			const scrollHeight = container.scrollHeight;
			const clientHeight = container.clientHeight;
			// No fim do scroll, marcar sempre a última seção (Criação em massa)
			if (scrollHeight - clientHeight <= scrollTop + 80) {
				setScrollActiveContatosSub(CONTATOS_SUB_IDS[CONTATOS_SUB_IDS.length - 1]);
				return;
			}
			// Offset = parte superior visível (35% da altura) para transição mais natural
			const offset = Math.max(150, clientHeight * 0.35);
			let current = null;
			for (const id of CONTATOS_SUB_IDS) {
				const el = document.getElementById(id);
				if (!el) continue;
				const top = el.offsetTop;
				if (top <= scrollTop + offset) current = id;
			}
			setScrollActiveContatosSub(current || "listar");
		};
		onScroll();
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [currentSection]);

	// Na página Funil de vendas, atualizar o item ativo da sidebar conforme o scroll
	useEffect(() => {
		if (currentSection !== "funil-vendas" || !contentRef.current) return;
		const container = contentRef.current;
		const onScroll = () => {
			const scrollTop = container.scrollTop;
			const scrollHeight = container.scrollHeight;
			const clientHeight = container.clientHeight;
			if (scrollHeight - clientHeight <= scrollTop + 80) {
				setScrollActiveFunilSub(FUNIL_SUB_IDS[FUNIL_SUB_IDS.length - 1]);
				return;
			}
			const offset = Math.max(150, clientHeight * 0.35);
			let current = null;
			for (const id of FUNIL_SUB_IDS) {
				const el = document.getElementById(id);
				if (!el) continue;
				const top = el.offsetTop;
				if (top <= scrollTop + offset) current = id;
			}
			setScrollActiveFunilSub(current || "listar");
		};
		onScroll();
		container.addEventListener("scroll", onScroll, { passive: true });
		return () => container.removeEventListener("scroll", onScroll);
	}, [currentSection]);

	return (
		<div style={effectiveStyles.root}>
			<style>{`
				.docs-nav-link:hover { color: #4f46e5; background-color: ${darkMode ? "rgba(255,255,255,0.06)" : "#f8fafc"}; }
				.docs-nav-sub:hover { color: #4f46e5; background-color: ${darkMode ? "rgba(255,255,255,0.06)" : "#f8fafc"}; }
				.docs-content-scroll { -ms-overflow-style: none; scrollbar-width: none; }
				.docs-content-scroll::-webkit-scrollbar { display: none; }
				.docs-right-panel { -ms-overflow-style: none; scrollbar-width: none; }
				.docs-right-panel::-webkit-scrollbar { display: none; }
			`}</style>
			<header style={effectiveStyles.header}>
				<h1 style={effectiveStyles.headerTitle}>Documentação API</h1>
				<div style={styles.headerRight}>
					<span style={effectiveStyles.headerVersion}>
						v{DOCS_VERSION}
						{DOCS_RELEASE_DATE ? ` · ${DOCS_RELEASE_DATE}` : ""}
					</span>
					<button
						type="button"
						onClick={() => setDarkMode((v) => !v)}
						title={darkMode ? "Modo claro" : "Modo escuro"}
						aria-label={darkMode ? "Ativar modo claro" : "Ativar modo escuro"}
						style={{
							...styles.headerDarkToggle,
							...(darkMode ? styles.headerDarkToggleDark : {}),
						}}
					>
						{darkMode ? <SunIcon /> : <MoonIcon />}
					</button>
				</div>
			</header>

			<div style={effectiveStyles.layout}>
				<aside style={effectiveStyles.sidebar}>
					<nav style={{ marginBottom: "1.5rem" }}>
						<div style={effectiveStyles.navGroupTitle}>Conteúdo</div>
						{navItems.map((item) => (
							<React.Fragment key={item.id}>
								<Link
									to={item.id === "contatos" ? "/docs/contatos" : item.id === "funil-vendas" ? "/docs/funil-vendas" : `/docs/${item.id}`}
									className="docs-nav-link"
									style={{
										...effectiveStyles.navLink,
										...(currentSection === item.id && (item.id === "introducao" ? !activeIntroSub : item.id === "contatos" ? !activeContatosSub : item.id === "funil-vendas" ? !activeFunilSub : true) ? effectiveStyles.navLinkActive : {}),
									}}
								>
									{item.label}
								</Link>
								{item.id === "introducao" && (
									<div style={{ paddingLeft: 0 }}>
										{INTRO_SUB.map((sub) => (
											<Link
												key={sub.id}
												to={`/docs/introducao#${sub.id}`}
												className="docs-nav-link docs-nav-sub"
												style={{
													...effectiveStyles.navSubLink,
													...(activeIntroSub === sub.id ? effectiveStyles.navSubLinkActive : {}),
												}}
											>
												{sub.label}
											</Link>
										))}
									</div>
								)}
								{item.id === "contatos" && (
									<div style={{ paddingLeft: 0 }}>
										{CONTATOS_SUB.map((sub) => (
											<Link
												key={sub.id}
												to={`/docs/contatos#${sub.id}`}
												className="docs-nav-link docs-nav-sub"
												style={{
													...effectiveStyles.navSubLink,
													...(activeContatosSub === sub.id ? effectiveStyles.navSubLinkActive : {}),
												}}
											>
												{sub.label}
											</Link>
										))}
									</div>
								)}
								{item.id === "funil-vendas" && (
									<div style={{ paddingLeft: 0 }}>
										{FUNIL_SUB.map((sub) => (
											<Link
												key={sub.id}
												to={`/docs/funil-vendas#${sub.id}`}
												className="docs-nav-link docs-nav-sub"
												style={{
													...effectiveStyles.navSubLink,
													...(activeFunilSub === sub.id ? effectiveStyles.navSubLinkActive : {}),
												}}
											>
												{sub.label}
											</Link>
										))}
									</div>
								)}
							</React.Fragment>
						))}
					</nav>
				</aside>

				<main ref={contentRef} className="docs-content-scroll" style={effectiveStyles.content}>
					{currentSection === "introducao" && (
						<>
							<section style={effectiveStyles.section} id="primeiros-passos">
								<h2 style={{ ...styles.h1, marginTop: 0 }}>Documentação da API</h2>
								<p style={effectiveStyles.paragraph}>
									Bem-vindo à documentação para desenvolvedores. Aqui você encontra tudo para integrar
									sistemas externos: primeiros passos, autenticação, Contatos, Funil de vendas e exemplos por página.
								</p>

								<h2 style={effectiveStyles.h2}>Primeiros passos</h2>
								<h3 style={effectiveStyles.h3}>1. Obtenha sua API Key</h3>
								<p style={effectiveStyles.paragraph}>
									Acesse o painel, faça login e vá em <strong>Configurações</strong>. Na seção da API Key você pode visualizar ou regenerar a chave da sua conta.
								</p>
								<h3 style={effectiveStyles.h3}>2. URL base</h3>
								<p style={effectiveStyles.paragraph}>Todas as requisições devem ser enviadas para:</p>
								<CodeBlock>{baseUrl}</CodeBlock>
								<h3 style={effectiveStyles.h3}>3. Autenticação</h3>
								<p style={effectiveStyles.paragraph}>
									Para integrações: troque sua API Key por um token JWT (<code style={effectiveStyles.inlineCode}>POST {baseUrl}/auth/token</code>). Para testes manuais: use login com usuário e senha. Detalhes no submenu <strong>Autenticação</strong>.
								</p>
							</section>

							<section style={effectiveStyles.section} id="autenticacao">
								<h2 style={{ ...effectiveStyles.h2, marginTop: 0 }}>Autenticação</h2>
							<h3 style={effectiveStyles.h3}>API externa (recomendado para integrações)</h3>
							<p style={effectiveStyles.paragraph}>
								Chame o endpoint abaixo enviando sua API Key no header <code style={effectiveStyles.inlineCode}>X-API-Key</code>; use o token retornado no Bearer das demais requisições.
							</p>
							<table style={effectiveStyles.table}>
								<thead>
									<tr>
										<th style={effectiveStyles.th}>Método</th>
										<th style={effectiveStyles.th}>Endpoint</th>
										<th style={effectiveStyles.th}>Descrição</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td style={effectiveStyles.td}><span style={styles.badgePost}>POST</span></td>
										<td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>{baseUrl}/auth/token</code></td>
										<td style={effectiveStyles.td}>Troca a API Key por um token JWT temporário</td>
									</tr>
								</tbody>
							</table>
							<h4 style={effectiveStyles.h3}>Header (obrigatório)</h4>
							<table style={effectiveStyles.table}>
								<thead>
									<tr>
										<th style={effectiveStyles.th}>Header</th>
										<th style={effectiveStyles.th}>Obrigatório</th>
										<th style={effectiveStyles.th}>Descrição</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>X-API-Key</code></td>
										<td style={effectiveStyles.td}>Sim</td>
										<td style={effectiveStyles.td}>Sua API Key (obtida em Configurações no painel)</td>
									</tr>
								</tbody>
							</table>
							<p style={effectiveStyles.paragraph}>Envie sua API Key no header <code style={effectiveStyles.inlineCode}>X-API-Key</code>. Não é necessário body.</p>
							<h4 style={effectiveStyles.h3}>Resposta (200) — JSON</h4>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "expiresIn": 3600
}`}</CodeBlock>
							<p style={effectiveStyles.paragraph}>
								Use o <code style={effectiveStyles.inlineCode}>token</code> no header de todas as demais requisições (Contatos, Funil de vendas): <code style={effectiveStyles.inlineCode}>Authorization: Bearer &lt;token_retornado&gt;</code>. Assim o front (login com usuário/senha) e as integrações (API Key → token) ficam separados.
							</p>
							<p style={effectiveStyles.paragraph}>
								A API Key é visualizada e regenerada apenas pela <strong>interface</strong> (Configurações no painel), não por endpoints da API.
							</p>
							</section>
						</>
					)}

					{currentSection === "contatos" && (
						<section style={effectiveStyles.section}>
							<h2 style={{ ...effectiveStyles.h2, marginTop: 0 }}>Contatos</h2>
							<p style={effectiveStyles.lastUpdate}>Última atualização da API: {CONTATOS_API_LAST_UPDATE}</p>
							<p style={effectiveStyles.paragraph}>
								A API de Contatos permite gerenciar a base de contatos da sua conta: buscar, criar, editar e remover contatos, além de criação em massa. Todas as requisições exigem autenticação via <strong>JWT</strong> (token obtido no login).
							</p>
							<h3 style={effectiveStyles.h3}>Resumo dos endpoints</h3>
							<table style={effectiveStyles.table}>
								<thead>
									<tr>
										<th style={effectiveStyles.th}>Método</th>
										<th style={effectiveStyles.th}>Operação</th>
										<th style={effectiveStyles.th}>Endpoint</th>
									</tr>
								</thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><span style={styles.badgeGet}>GET</span></td><td style={effectiveStyles.td}>Listar</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/contacts</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgeGet}>GET</span></td><td style={effectiveStyles.tdAlt}>Obter um</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/contacts/:contactId</code></td></tr>
									<tr><td style={effectiveStyles.td}><span style={styles.badgePost}>POST</span></td><td style={effectiveStyles.td}>Criar</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/contacts</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgePut}>PUT</span></td><td style={effectiveStyles.tdAlt}>Atualizar</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/contacts/:contactId</code></td></tr>
									<tr><td style={effectiveStyles.td}><span style={styles.badgeDelete}>DELETE</span></td><td style={effectiveStyles.td}>Remover</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/contacts/:contactId</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgePost}>POST</span></td><td style={effectiveStyles.tdAlt}>Criação em massa</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/contacts/bulk-create</code></td></tr>
								</tbody>
							</table>

							<h2 id="listar" style={effectiveStyles.h2}>Listar contatos</h2>
							<p style={effectiveStyles.paragraph}>
								Retorna uma lista paginada de contatos. Requisição <span style={styles.badgeGet}>GET</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetros de query (opcionais)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>searchParam</code></td><td style={effectiveStyles.td}>Busca por nome ou número</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>pageNumber</code></td><td style={effectiveStyles.tdAlt}>Número da página</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>tags</code></td><td style={effectiveStyles.td}>Filtrar por etiquetas</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>channel</code></td><td style={effectiveStyles.tdAlt}>Canal (ex.: whatsapp)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "contacts": [
    { "id": 1, "name": "João", "number": "5511999999999", "email": "joao@email.com", "company": null, "channel": "whatsapp" }
  ],
  "count": 1,
  "hasMore": false
}`}</CodeBlock>

							<h2 id="obter" style={effectiveStyles.h2}>Obter um contato</h2>
							<p style={effectiveStyles.paragraph}>
								Retorna os dados de um contato pelo ID. Requisição <span style={styles.badgeGet}>GET</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts/:contactId</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetro de path</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contactId</code></td><td style={effectiveStyles.td}>ID do contato</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "id": 1,
  "name": "João",
  "number": "5511999999999",
  "email": "joao@email.com",
  "company": null,
  "channel": "whatsapp",
  "extraInfo": []
}`}</CodeBlock>

							<h2 id="criar" style={effectiveStyles.h2}>Criar contato</h2>
							<p style={effectiveStyles.paragraph}>
								Cria um novo contato. Requisição <span style={styles.badgePost}>POST</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Obrigatório</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>name</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Sim</td><td style={effectiveStyles.td}>Nome do contato</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>number</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Não</td><td style={effectiveStyles.tdAlt}>Número (apenas dígitos)</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>email</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Não</td><td style={effectiveStyles.td}>E-mail</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>channel</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Não</td><td style={effectiveStyles.tdAlt}>Canal (ex.: whatsapp)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "id": 1,
  "name": "João",
  "number": "5511999999999",
  "email": "joao@email.com",
  "company": null,
  "channel": "whatsapp",
  "extraInfo": []
}`}</CodeBlock>

							<h2 id="atualizar" style={effectiveStyles.h2}>Atualizar contato</h2>
							<p style={effectiveStyles.paragraph}>
								Atualiza um contato existente. Requisição <span style={styles.badgePut}>PUT</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts/:contactId</code>. Envie no body apenas os campos que deseja atualizar.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetro de path</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contactId</code></td><td style={effectiveStyles.td}>ID do contato a atualizar</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>name</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Nome do contato</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>number</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Número (apenas dígitos)</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>email</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>E-mail</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>company</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Empresa</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>channel</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Canal (ex.: whatsapp)</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>extraInfo</code></td><td style={effectiveStyles.tdAlt}>array</td><td style={effectiveStyles.tdAlt}>Campos extras (objetos com name e value)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "id": 1,
  "name": "João",
  "number": "5511999999999",
  "email": "joao@email.com",
  "company": null,
  "channel": "whatsapp",
  "extraInfo": []
}`}</CodeBlock>

							<h2 id="remover" style={effectiveStyles.h2}>Remover contato</h2>
							<p style={effectiveStyles.paragraph}>
								Remove um contato. Requisição <span style={styles.badgeDelete}>DELETE</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts/:contactId</code>. Não envia body.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetro de path</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contactId</code></td><td style={effectiveStyles.td}>ID do contato a remover</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "message": "Contact deleted"
}`}</CodeBlock>

							<h2 id="bulk-create" style={effectiveStyles.h2}>Criação em massa</h2>
							<p style={effectiveStyles.paragraph}>
								Cria vários contatos de uma vez. Requisição <span style={styles.badgePost}>POST</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/contacts/bulk-create</code>. O body deve ser um objeto com a propriedade <code style={effectiveStyles.inlineCode}>contacts</code>, um array de objetos no formato de um contato.
							</p>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contacts</code></td><td style={effectiveStyles.td}>array</td><td style={effectiveStyles.td}>Lista de contatos a criar</td></tr>
								</tbody>
							</table>
							<p style={effectiveStyles.paragraph}>Cada item do array <code style={effectiveStyles.inlineCode}>contacts</code> pode ter:</p>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Obrigatório</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>name</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Sim</td><td style={effectiveStyles.td}>Nome do contato</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>number</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Sim</td><td style={effectiveStyles.tdAlt}>Número (apenas dígitos)</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>email</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Não</td><td style={effectiveStyles.td}>E-mail</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "count": 2,
  "contacts": [
    { "id": 1, "name": "João", "number": "5511999999999", "email": "joao@email.com" },
    { "id": 2, "name": "Maria", "number": "5511888888888", "email": "maria@email.com" }
  ]
}`}</CodeBlock>
						</section>
					)}

					{currentSection === "funil-vendas" && (
						<section style={effectiveStyles.section}>
							<h2 style={{ ...effectiveStyles.h2, marginTop: 0 }}>Funil de vendas</h2>
							<p style={effectiveStyles.lastUpdate}>Última atualização da API: {FUNIL_API_LAST_UPDATE}</p>
							<p style={effectiveStyles.paragraph}>
								API para gerenciar funis de vendas: listar, criar, atualizar e remover funis, além de fixar e desfixar contatos em um funil. Todas as requisições exigem autenticação via <strong>JWT</strong> (token obtido no login).
							</p>
							<h3 style={effectiveStyles.h3}>Resumo dos endpoints</h3>
							<table style={effectiveStyles.table}>
								<thead>
									<tr>
										<th style={effectiveStyles.th}>Método</th>
										<th style={effectiveStyles.th}>Operação</th>
										<th style={effectiveStyles.th}>Endpoint</th>
									</tr>
								</thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><span style={styles.badgeGet}>GET</span></td><td style={effectiveStyles.td}>Listar</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/sales-funnel</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgePost}>POST</span></td><td style={effectiveStyles.tdAlt}>Criar</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/sales-funnel</code></td></tr>
									<tr><td style={effectiveStyles.td}><span style={styles.badgePut}>PUT</span></td><td style={effectiveStyles.td}>Atualizar</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/sales-funnel/:id</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgeDelete}>DELETE</span></td><td style={effectiveStyles.tdAlt}>Remover</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/sales-funnel/:id</code></td></tr>
									<tr><td style={effectiveStyles.td}><span style={styles.badgePost}>POST</span></td><td style={effectiveStyles.td}>Fixar contato</td><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>/sales-funnel/pin-contact</code></td></tr>
									<tr><td style={effectiveStyles.tdAlt}><span style={styles.badgePost}>POST</span></td><td style={effectiveStyles.tdAlt}>Desfixar contato</td><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>/sales-funnel/unpin-contact</code></td></tr>
								</tbody>
							</table>

							<h2 id="listar" style={effectiveStyles.h2}>Listar funis</h2>
							<p style={effectiveStyles.paragraph}>
								Retorna a lista de funis. Requisição <span style={styles.badgeGet}>GET</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetros de query (opcionais)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>workspace</code></td><td style={effectiveStyles.td}>Workspace (ex.: default)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`[
  { "id": 1, "name": "Qualificação", "workspace": "default", "position": 1, "color": "#4f46e5", "cardColor": "#eef2ff" },
  { "id": 2, "name": "Proposta", "workspace": "default", "position": 2, "color": "#059669", "cardColor": "#d1fae5" }
]`}</CodeBlock>

							<h2 id="criar" style={effectiveStyles.h2}>Criar funil</h2>
							<p style={effectiveStyles.paragraph}>
								Cria um novo funil. Requisição <span style={styles.badgePost}>POST</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Obrigatório</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>name</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Sim</td><td style={effectiveStyles.td}>Nome do funil</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>workspace</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Sim</td><td style={effectiveStyles.tdAlt}>Workspace (ex.: default)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "id": 1,
  "name": "Qualificação",
  "workspace": "default",
  "position": 1,
  "color": "#4f46e5",
  "cardColor": "#eef2ff"
}`}</CodeBlock>

							<h2 id="atualizar" style={effectiveStyles.h2}>Atualizar funil</h2>
							<p style={effectiveStyles.paragraph}>
								Atualiza um funil existente. Requisição <span style={styles.badgePut}>PUT</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel/:id</code>. Envie no body os campos a atualizar.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetro de path</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>id</code></td><td style={effectiveStyles.td}>ID do funil</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>name</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Nome do funil</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>position</code></td><td style={effectiveStyles.tdAlt}>number</td><td style={effectiveStyles.tdAlt}>Posição</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>color</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Cor</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>cardColor</code></td><td style={effectiveStyles.tdAlt}>string</td><td style={effectiveStyles.tdAlt}>Cor do card</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (200) — JSON</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Exemplo de resposta">{`{
  "id": 1,
  "name": "Qualificação",
  "workspace": "default",
  "position": 1,
  "color": "#4f46e5",
  "cardColor": "#eef2ff"
}`}</CodeBlock>

							<h2 id="remover" style={effectiveStyles.h2}>Remover funil</h2>
							<p style={effectiveStyles.paragraph}>
								Remove um funil. Requisição <span style={styles.badgeDelete}>DELETE</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel/:id</code>. Não envia body.
							</p>
							<h3 style={effectiveStyles.h3}>Parâmetro de path</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Parâmetro</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>id</code></td><td style={effectiveStyles.td}>ID do funil a remover</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (204 No Content)</h3>
							<p style={effectiveStyles.paragraph}>Corpo vazio. A API sempre retorna JSON quando há corpo; em 204 não há corpo.</p>
							<CodeBlock themeStyles={effectiveStyles} label="Resposta">{"(corpo vazio)"}</CodeBlock>

							<h2 id="pin-contact" style={effectiveStyles.h2}>Fixar contato</h2>
							<p style={effectiveStyles.paragraph}>
								Fixa um contato em um funil. Requisição <span style={styles.badgePost}>POST</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel/pin-contact</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contactId</code></td><td style={effectiveStyles.td}>number</td><td style={effectiveStyles.td}>ID do contato</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>salesFunnelId</code></td><td style={effectiveStyles.tdAlt}>number</td><td style={effectiveStyles.tdAlt}>ID do funil</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>description</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Descrição (opcional)</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>expiresIn</code></td><td style={effectiveStyles.tdAlt}>string/number</td><td style={effectiveStyles.tdAlt}>Expiração (opcional)</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>cover</code></td><td style={effectiveStyles.td}>string</td><td style={effectiveStyles.td}>Capa (opcional)</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>attachments</code></td><td style={effectiveStyles.tdAlt}>array</td><td style={effectiveStyles.tdAlt}>Anexos (opcional)</td></tr>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>userId</code></td><td style={effectiveStyles.td}>number</td><td style={effectiveStyles.td}>ID do usuário (opcional)</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>valor</code></td><td style={effectiveStyles.tdAlt}>number</td><td style={effectiveStyles.tdAlt}>Valor (opcional)</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (204 No Content)</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Resposta">{"(corpo vazio)"}</CodeBlock>

							<h2 id="unpin-contact" style={effectiveStyles.h2}>Desfixar contato</h2>
							<p style={effectiveStyles.paragraph}>
								Remove um contato de um funil. Requisição <span style={styles.badgePost}>POST</span> para <code style={effectiveStyles.inlineCode}>{baseUrl}/sales-funnel/unpin-contact</code>.
							</p>
							<h3 style={effectiveStyles.h3}>Body (JSON)</h3>
							<table style={effectiveStyles.table}>
								<thead><tr><th style={effectiveStyles.th}>Campo</th><th style={effectiveStyles.th}>Tipo</th><th style={effectiveStyles.th}>Descrição</th></tr></thead>
								<tbody>
									<tr><td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>contactId</code></td><td style={effectiveStyles.td}>number</td><td style={effectiveStyles.td}>ID do contato</td></tr>
									<tr><td style={effectiveStyles.tdAlt}><code style={effectiveStyles.inlineCode}>salesFunnelId</code></td><td style={effectiveStyles.tdAlt}>number</td><td style={effectiveStyles.tdAlt}>ID do funil</td></tr>
								</tbody>
							</table>
							<h3 style={effectiveStyles.h3}>Resposta (204 No Content)</h3>
							<CodeBlock themeStyles={effectiveStyles} label="Resposta">{"(corpo vazio)"}</CodeBlock>
						</section>
					)}

					{currentSection === "erros" && (
						<section style={effectiveStyles.section}>
							<h2 style={{ ...effectiveStyles.h2, marginTop: 0 }}>Erros</h2>
							<p style={effectiveStyles.paragraph}>Códigos HTTP em falhas de autenticação ou autorização:</p>
							<table style={effectiveStyles.table}>
								<thead>
									<tr>
										<th style={effectiveStyles.th}>Código</th>
										<th style={effectiveStyles.th}>Mensagem / Condição</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td style={effectiveStyles.td}><span style={styles.badgeError}>401</span></td>
										<td style={effectiveStyles.td}><code style={effectiveStyles.inlineCode}>ERR_SESSION_EXPIRED</code> — Header ausente ou token inválido.</td>
									</tr>
									<tr>
										<td style={effectiveStyles.tdAlt}><span style={styles.badgeError}>403</span></td>
										<td style={effectiveStyles.tdAlt}>Token inválido ou revogado.</td>
									</tr>
								</tbody>
							</table>
							<p style={effectiveStyles.paragraph}>
								Use <code style={effectiveStyles.inlineCode}>Authorization: Bearer &lt;token&gt;</code> e verifique se o token está correto.
							</p>
						</section>
					)}
				</main>

				<aside className="docs-right-panel" style={effectiveStyles.rightPanel}>
					<RightPanelExamples section={currentSection} baseUrl={baseUrl} introSub={activeIntroSub} contatosSub={activeContatosSub} funilSub={activeFunilSub} themeStyles={effectiveStyles} />
				</aside>
			</div>
		</div>
	);
}
